import React from 'react';
// Use VoyagerSearchContext for Voyager API integration
import { useVoyagerSearch as useSearch } from '../../context/VoyagerSearchContext';
import type { FilterItem } from '../../models/search-result';
import FilterCategoryNode from './FilterCategoryNode';
import { TREE_NSDI_SECTION_ID } from './FilterSidebar.constants';
import {
  buildCategoryTree,
  collectHierarchyStateKeys,
} from './FilterSidebar.helpers';
import { useFilterSidebarState } from './useFilterSidebarState';
import './FilterSidebar.scss';

interface FilterSidebarProps {
  onFilterChange?: (filters: FilterItem[]) => void;
  valueCategoriesCollapsedByDefault?: boolean;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  onFilterChange,
  valueCategoriesCollapsedByDefault = true,
}) => {
  const { searchState, applySearchParams, facets } = useSearch();

  // Prefer the search-result facets so the counts reflect the active result
  // set (keyword search, filters, scope and spatial constraints). Sections that
  // declare a Solr exclude tag still show their sibling values after selection.
  // Fall back to the standalone facet fetch only until the first search
  // response arrives.
  const searchResultSections = searchState.results?.filterSections;
  const filterSections = searchResultSections?.length
    ? searchResultSections
    : facets || [];

  const categoryTree = buildCategoryTree(filterSections);
  const hierarchyStateKeys = collectHierarchyStateKeys(categoryTree);
  const {
    expandedSections,
    showMoreStates,
    isHierarchyExpanded,
    toggleSection,
    toggleShowMore,
    toggleHierarchy,
  } = useFilterSidebarState(
    hierarchyStateKeys,
    valueCategoriesCollapsedByDefault,
  );

  const handleFilterToggle = (sectionId: string, filterItem: FilterItem) => {
    // Get current filters from search state
    const currentFilters = searchState.query?.filters || [];

    // Check if this filter is already selected
    const isSelected = currentFilters.some(
      (f) => f.sectionId === sectionId && f.id === filterItem.id,
    );

    // Update filters - add or remove the filter
    let updatedFilters;
    if (isSelected) {
      // Remove the filter
      updatedFilters = currentFilters.filter(
        (f) => !(f.sectionId === sectionId && f.id === filterItem.id),
      );
    } else {
      const retainedFilters =
        sectionId === TREE_NSDI_SECTION_ID
          ? currentFilters.filter((f) => f.sectionId !== TREE_NSDI_SECTION_ID)
          : currentFilters;

      updatedFilters = [
        ...retainedFilters,
        {
          sectionId: sectionId,
          label: filterItem.label,
          id: filterItem.id,
          isChecked: true,
        },
      ];
    }

    applySearchParams({ filters: updatedFilters, pageIndex: 0 });
    if (onFilterChange) {
      onFilterChange(updatedFilters);
    }
  };

  const isFilterChecked = (sectionId: string, filterItem: FilterItem) =>
    (searchState.query?.filters || []).some(
      (f) => f.sectionId === sectionId && f.id === filterItem.id,
    );

  return (
    <div className="filter-sidebar">
      {/* Filters Section */}
      <div className="filter-section">
        <button
          type="button"
          className="filter-section-header"
          onClick={() => toggleSection('filters')}
        >
          <span
            className={`expand-icon ${expandedSections.filters ? 'expanded' : ''}`}
          >
            ▼
          </span>
          <span className="section-title filters">Filters</span>
        </button>
        {expandedSections.filters && (
          <div className="filter-section-content">
            {hierarchyStateKeys.length > 0 && (
              <div className="filter-hierarchy-actions">
                <button
                  type="button"
                  className="filter-hierarchy-toggle"
                  onClick={toggleHierarchy}
                >
                  {isHierarchyExpanded
                    ? 'Minimize hierarchy'
                    : 'Maximize hierarchy'}
                </button>
              </div>
            )}
            {/* Dynamically render filter sections from API */}
            {categoryTree.map((node) => (
              <FilterCategoryNode
                key={node.id}
                node={node}
                expandedSections={expandedSections}
                showMoreStates={showMoreStates}
                valueCategoriesCollapsedByDefault={
                  valueCategoriesCollapsedByDefault
                }
                onToggleSection={toggleSection}
                onToggleShowMore={toggleShowMore}
                onFilterToggle={handleFilterToggle}
                isFilterChecked={isFilterChecked}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSidebar;
